import { Avatar, Box, Button, Card, CardContent, CardMedia, Collapse, FormControl, IconButton, List, ListItem, ListItemAvatar, ListItemText, Paper, Slider, Switch, ToggleButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import io, { Socket } from 'socket.io-client';
import { SceneParameters } from "./scenes";
import { scenes } from "./scenes";
import { ParameterBox } from "./main";
import { ExpandMore, CameraAltOutlined, MicNoneOutlined, AdsClickOutlined, FastForwardOutlined } from '@mui/icons-material';

type ConnectionStatus = 'pageOpened' | 'awaitingParameters' | 'connected' | 'disconnected';

export type MessagePayload = {
    sessionId: string;
    messageType: 'command' | 'currentParameters' | 'parameterUpdate' | 'sceneUpdate' | 'generalSettingsUpdate' | 'audioSettingsUpdate';
    message: string;
}

export type GeneralSettings = {
    camera: boolean,
    microphone: boolean,
    cursorAnimation: boolean,
    skipTransitions: boolean
}

export type AudioSettings = {
    bpm: number;
    threshold: number;
    frequencyRange: [number, number];
};

export type AudioSettingUpdate = {
    setting: string;
    value: number | [number, number];
}

export type CurrentParameters = {
    generalSettings: GeneralSettings;
    audioSettings: AudioSettings | undefined;
    scene: number;
    sceneParameters: SceneParameters | [];
}

export type ParameterUpdate = {
    boxIndex: number;
    parameterIndex: number;
    value: number;
}

const RemoteControl = () => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [status, setStatus] = useState<ConnectionStatus>('pageOpened');
    const [sessionId, setSessionId] = useState<string>();
    const [generalSettings, setGeneralSettings] = useState<GeneralSettings>();
    const [audioSettings, setAudioSettings] = useState<AudioSettings>();
    const [scene, setScene] = useState<number>();
    const [sceneParameters, setSceneParameters] = useState<SceneParameters | []>([])

    //const [data, setData] = useState<string>()

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const sessionIdFromURL = queryParams.get('sessionId');

        if (sessionIdFromURL) {
            setSessionId(sessionIdFromURL);
            const link = 'https://api.parametric-jam.com/remote';   // 'http://192.168.1.161:8001/remote'  // 'https://fragrance-fourth-density-sept.trycloudflare.com/remote';
            const socket = io(link, {
                query: { sessionId: sessionIdFromURL }
            });
            setSocket(socket);
            socket.on('connect', () => {
                setStatus('awaitingParameters');
                socket.emit('register', { sessionId: sessionIdFromURL });
            });

            socket.on('message', (payload: MessagePayload) => {
                switch(payload.messageType) {
                    case 'command':
                        break;
                    case 'currentParameters':
                        const currentParameters: CurrentParameters = JSON.parse(payload.message);
                        console.log(currentParameters)
                        //setData(JSON.stringify(currentParameters))
                        setGeneralSettings(currentParameters.generalSettings);
                        setAudioSettings(currentParameters.audioSettings);
                        setScene(currentParameters.scene);
                        setSceneParameters(currentParameters.sceneParameters);
                        const connectionEstablishedMessage: MessagePayload = {
                            sessionId: sessionIdFromURL,
                            messageType: 'command',
                            message: 'connectionEstablished'
                        }
                        socket.emit('message', connectionEstablishedMessage );
                        setStatus('connected');
                        break;
                    case 'parameterUpdate':
                        break;
                    case 'sceneUpdate':
                        break;
                    case "generalSettingsUpdate":
                        break;
                    case "audioSettingsUpdate":
                        break;
                }
            });
        
            socket.on('disconnect', () => {
                setStatus('disconnected');
            });

            return () => {
                socket.close();
            };
        }
    }, []);

    const handleSceneChange = (sceneId: number) => {
        if (socket && sessionId) {
            const sceneUpdateMessage: MessagePayload = {
                sessionId: sessionId!,
                messageType: 'sceneUpdate',
                message: sceneId.toString()
            }
            socket!.emit('message', sceneUpdateMessage );
        }
    }

    const handleGeneralSettingsChange = (setting: keyof GeneralSettings) => {
        if (socket && sessionId) {
            const settingsUpdateMessage: MessagePayload = {
                sessionId: sessionId!,
                messageType: 'generalSettingsUpdate',
                message: setting
            }
            socket!.emit('message', settingsUpdateMessage );
        }
    };

    const handleAudioSettingsChange = (setting: keyof AudioSettings, value: number | [number, number]) => {
        if (socket && sessionId) {
            const settingUpdate: AudioSettingUpdate = {
                setting,
                value
            }
            const settingsUpdateMessage: MessagePayload = {
                sessionId: sessionId!,
                messageType: 'audioSettingsUpdate',
                message: JSON.stringify(settingUpdate)
            }
            socket!.emit('message', settingsUpdateMessage );
        }
    };

    const handleParameterUpdate = (boxIndex: number, parameterIndex: number, value: number) => {
        if (socket && sessionId) {
            const parameterUpdate: ParameterUpdate = {
                boxIndex,
                parameterIndex,
                value
            }
            const sceneUpdateMessage: MessagePayload = {
                sessionId: sessionId!,
                messageType: 'parameterUpdate',
                message: JSON.stringify(parameterUpdate)
            }
            socket.emit('message', sceneUpdateMessage );
        }
    }
    
    return (
        <Box sx={{width: "100vw", height: "100dvh", position: 'absolute', backgroundColor: 'rgb(70, 70, 70)', overflowY: 'auto'}}>
            {status !== 'connected' && <Typography sx={{color: 'white'}}>{status}</Typography>}
            {status === 'connected' && scene != undefined && generalSettings != undefined &&
                <SceneParametersControls 
                    generalSettings={generalSettings}
                    audioSettings={audioSettings}
                    scene={scene!}
                    sceneParameters={sceneParameters} 
                    handleGeneralSettingsChange={handleGeneralSettingsChange}
                    handleAudioSettingsChange={handleAudioSettingsChange}
                    handleSceneChange={handleSceneChange}
                    handleParameterUpdate={handleParameterUpdate}
                />
            }
        </Box>
    )
}

export default RemoteControl

interface SceneParametersControlsProps {
    generalSettings: GeneralSettings;
    audioSettings: AudioSettings | undefined;
    scene: number;
    sceneParameters: SceneParameters | [],
    handleGeneralSettingsChange: (setting: keyof GeneralSettings) => void;
    handleAudioSettingsChange: (setting: keyof AudioSettings, value: number | [number, number]) => void;
    handleSceneChange: (scene: number) => void;
    handleParameterUpdate: (boxIndex: number, parameterIndex: number, value: number) => void;
}

const SceneParametersControls = ({generalSettings, audioSettings, scene, sceneParameters, handleGeneralSettingsChange, handleAudioSettingsChange, handleSceneChange, handleParameterUpdate}:SceneParametersControlsProps) => {
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 300px))',
            gridAutoRows: '250px',
            gap: '15px',
            justifyContent: 'center',
            alignItems: 'center',
            overflowY: 'auto',
            width: '100%', 
            height: '100%', 
            padding: '15px',
            boxSizing: 'border-box',
        }}>
            <SceneCard scene={scene} onSceneChange={handleSceneChange}/>
            {
                sceneParameters.map((propBox, index) => {
                    switch(propBox.category) {
                        case 'audio':
                            if (scenes[scene].audioReactive && generalSettings.microphone) return <ParameterBox scene={scene} boxIndex={index} key={index} sceneParameters={sceneParameters} handleSetSceneParameters={handleParameterUpdate}/>
                            break;
                        case 'video':
                            if (scenes[scene].videoResponsive && generalSettings.camera) return <ParameterBox scene={scene} boxIndex={index} key={index} sceneParameters={sceneParameters} handleSetSceneParameters={handleParameterUpdate}/>
                            break;
                        case 'global':
                            return <ParameterBox scene={scene} boxIndex={index} key={index} sceneParameters={sceneParameters} handleSetSceneParameters={handleParameterUpdate}/>
                    }
                })
            }
            <GeneralSettingsBox generalSettings={generalSettings} handleGeneralSettingsChange={handleGeneralSettingsChange} />
            {audioSettings && <AudioSettingsBox audioSettings={audioSettings} handleAudioSettingsChange={handleAudioSettingsChange} />}
        </Box>
    )
}

interface GeneralSettingsBoxProps {
    generalSettings: GeneralSettings;
    handleGeneralSettingsChange: (setting: keyof GeneralSettings) => void;
}

const GeneralSettingsBox = ({ generalSettings, handleGeneralSettingsChange }: GeneralSettingsBoxProps) => {
    return (
        <Paper elevation={3} sx={{
            height: "100%",
            minWidth: '220px',
            maxWidth: '300px',
            padding: '5px 25px 10px 25px',
            backgroundColor: 'rgb(75, 75, 75)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2
        }}>
            <Typography variant="h6" sx={{ color: 'white', marginBottom: '5px', textAlign: 'center' }}>
                General Settings
            </Typography>
            <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                <ToggleButton
                    value="camera"
                    selected={generalSettings.camera}
                    onChange={() => handleGeneralSettingsChange('camera')}
                    sx={{ flexGrow: 1, flexDirection: 'column', padding: '10px 6px', gap: '2px', color: 'white !important', backgroundColor: 'rgb(75, 75, 75)', '&.Mui-selected': { backgroundColor: 'rgb(90, 90, 90) !important', border: '2px solid white' } }}
                >
                    <CameraAltOutlined sx={{ marginBottom: '5px' }} />
                    <Typography fontSize={'10px'}>Camera</Typography>
                </ToggleButton>
                <ToggleButton
                    value="microphone"
                    selected={generalSettings.microphone}
                    onChange={() => handleGeneralSettingsChange('microphone')}
                    sx={{ flexGrow: 1, flexDirection: 'column', padding: '10px 6px', gap: '2px', color: 'white !important', backgroundColor: 'rgb(75, 75, 75)', '&.Mui-selected': { backgroundColor: 'rgb(90, 90, 90) !important', border: '2px solid white' } }}
                >
                    <MicNoneOutlined sx={{ marginBottom: '5px' }} />
                    <Typography fontSize={'10px'}>Microphone</Typography>
                </ToggleButton>
                <ToggleButton
                    value="cursor"
                    selected={generalSettings.cursorAnimation}
                    onChange={() => handleGeneralSettingsChange('cursorAnimation')}
                    sx={{ flexGrow: 1, flexDirection: 'column', padding: '10px 6px', gap: '2px', color: 'white !important', backgroundColor: 'rgb(75, 75, 75)', '&.Mui-selected': { backgroundColor: 'rgb(90, 90, 90) !important', border: '2px solid white' } }}
                >
                    <AdsClickOutlined sx={{ marginBottom: '5px' }} />
                    <Typography fontSize={'10px'}>Cursor Animation</Typography>
                </ToggleButton>
                <ToggleButton
                    value="skip"
                    selected={generalSettings.skipTransitions}
                    onChange={() => handleGeneralSettingsChange('skipTransitions')}
                    sx={{ flexGrow: 1, flexDirection: 'column', padding: '10px 6px', gap: '2px', color: 'white !important', backgroundColor: 'rgb(75, 75, 75)', '&.Mui-selected': { backgroundColor: 'rgb(90, 90, 90) !important', border: '2px solid white' } }}
                >
                    <FastForwardOutlined sx={{ marginBottom: '5px' }} />
                    <Typography fontSize={'10px'}>Skip Transitions</Typography>
                </ToggleButton>
            </Box>
        </Paper>
    );
};

interface AudioSettingsBoxProps {
    audioSettings: AudioSettings;
    handleAudioSettingsChange: (setting: keyof AudioSettings, value: number | [number, number]) => void;
}

const AudioSettingsBox = ({ audioSettings, handleAudioSettingsChange }: AudioSettingsBoxProps) => {
    const scale = (x: number) => Math.log10(x + 10) * 1000;
    const valueLabelFormat = (value: number) => Math.pow(10, value / 1000) - 10;
    return (
        <Paper elevation={3} sx={{
            height: "100%",
            minWidth: '220px',
            maxWidth: '300px',
            padding: '5px 25px 10px 25px',
            backgroundColor: 'rgb(75, 75, 75)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        }}>
            <Typography variant="h6" component="h3" sx={{color: 'white', marginBottom: '5px'}} align='center'>Audioreactive Settings</Typography>
            <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    flexGrow: 1,
                    width: '100%'
            }}>
                    <Typography sx={{
                                color: 'white',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                    }}>Beats Per Minute</Typography>
                    <Slider
                        min={40}
                        max={300}
                        value={audioSettings.bpm}
                        onChange={(e, value) => handleAudioSettingsChange('bpm', value as number)}
                        valueLabelDisplay="auto"
                        sx={{
                            color: 'white',
                            '& .MuiSlider-thumb': {
                                '&:hover, &.Mui-focusVisible': {
                                    boxShadow: `0px 0px 0px 8px rgba(255, 255, 255, 0.16)`
                                },
                                '&.Mui-active': {
                                    boxShadow: `0px 0px 0px 14px rgba(255, 255, 255, 0.16)`
                                }
                            },
                        }}
                    />
                    <Typography sx={{
                                color: 'white',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                    }}>Threshold</Typography>
                    <Slider
                        min={0.05}
                        max={0.8}
                        step={0.01}
                        value={audioSettings.threshold}
                        onChange={(e, value) => handleAudioSettingsChange('threshold', value as number)}
                        valueLabelDisplay="auto"
                        sx={{
                            color: 'white',
                            '& .MuiSlider-thumb': {
                                '&:hover, &.Mui-focusVisible': {
                                    boxShadow: `0px 0px 0px 8px rgba(255, 255, 255, 0.16)`
                                },
                                '&.Mui-active': {
                                    boxShadow: `0px 0px 0px 14px rgba(255, 255, 255, 0.16)`
                                }
                            },
                        }}
                    />
                    <Typography sx={{
                                color: 'white',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                    }}>Beat Detection Frequency</Typography>
                    <Slider
                        min={scale(20)}
                        max={scale(14000)}
                        step={10}
                        value={[scale(audioSettings.frequencyRange[0]), scale(audioSettings.frequencyRange[1])]}
                        onChange={(e, newValue) => {
                            if (Array.isArray(newValue)) {
                                handleAudioSettingsChange('frequencyRange', newValue.map(valueLabelFormat) as [number, number]);
                            }
                        }}
                        valueLabelDisplay="auto"
                        valueLabelFormat={value => Math.round(valueLabelFormat(value))}
                        sx={{
                            color: 'white',
                            '& .MuiSlider-thumb': {
                                '&:hover, &.Mui-focusVisible': {
                                    boxShadow: `0px 0px 0px 8px rgba(255, 255, 255, 0.16)`
                                },
                                '&.Mui-active': {
                                    boxShadow: `0px 0px 0px 14px rgba(255, 255, 255, 0.16)`
                                }
                            },
                        }}
                    />
            </Box>
        </Paper>
    );
};

interface SceneCardProps {
    scene: number,
    onSceneChange: (scene: number) => void
}


const SceneCard = ({ scene, onSceneChange }: SceneCardProps) => {
    const [open, setOpen] = useState(false);

    const toggleDropdown = () => {
        setOpen(!open);
    };

    return (
        <Card sx={{ 
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'visible'
        }}> 
            <CardMedia
                component="img"
                height='175px'
                image={scenes[scene].image}
                alt={scenes[scene].title}
            />
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={toggleDropdown}>
                <Typography variant="h6">{scenes[scene].title}</Typography>
                <IconButton>
                    <ExpandMore />
                </IconButton>
            </CardContent>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List sx={{ width: '100%', maxWidth: '300px', bgcolor: 'background.paper', position: 'absolute', top: '99%', left: 0, maxHeight: '300px', overflowY: 'auto', zIndex: 1300 }}>
                    {scenes.map((sceneItem, index) => (
                        <ListItem button key={index} onClick={() => {
                            toggleDropdown();
                            onSceneChange(index);
                        }}>
                            <ListItemAvatar>
                                <Avatar src={sceneItem.image} alt={sceneItem.title} />
                            </ListItemAvatar>
                            <ListItemText primary={sceneItem.title} />
                        </ListItem>
                    ))}
                </List>
            </Collapse>
        </Card>
    );
};
